import { useEffect, useState } from "react";
import { Camera, ChevronLeft, ChevronRight, X, FolderOpen, Car } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useContent } from "@/contexts/ContentContext";

const FALLBACK = [
  {
    before: "https://images.pexels.com/photos/11291684/pexels-photo-11291684.jpeg",
    after: "https://images.pexels.com/photos/29755707/pexels-photo-29755707.jpeg",
    title: "Pack Complet — Berline",
  },
];

export default function BeforeAfter({ hideHeader = false }) {
  const { content } = useContent();
  const albums = content.albums || [];

  const [gallery, setGallery] = useState(FALLBACK);
  const [idx, setIdx] = useState(0);
  const [pos, setPos] = useState(50);
  const [openAlbum, setOpenAlbum] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("gallery_items")
          .select("*")
          .order("created_at", { ascending: false });
        if (!active) return;
        if (error) throw error;
        if (data && data.length > 0) {
          setGallery(data.map((g) => ({ before: g.before_url, after: g.after_url, title: g.title || "Avant / Après" })));
          setIdx(0);
        }
      } catch {
        /* repli sur les images de démo */
      }
    })();
    return () => { active = false; };
  }, []);

  const count = gallery.length;
  const next = () => { setIdx((idx + 1) % count); setPos(50); };
  const prev = () => { setIdx((idx - 1 + count) % count); setPos(50); };
  const current = gallery[idx] || FALLBACK[0];

  return (
    <section id="galerie" className="relative py-20 lg:py-28 border-t border-white/5" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row justify-between sm:items-end mb-10 gap-6">
          {!hideHeader ? (
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">/ Resultats</div>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4">
                Avant <span className="text-white/30">/</span> <span className="text-gradient-red">Apres.</span>
              </h2>
              <p className="text-white/60 leading-relaxed">Glissez le curseur pour comparer. La transformation parle d'elle-meme.</p>
            </div>
          ) : (
            <p className="text-white/60 text-sm">Glissez le curseur pour comparer la transformation.</p>
          )}

          <div className="flex items-center gap-3 shrink-0">
            <button onClick={prev} data-testid="gallery-prev" aria-label="Precedent"
              className="w-11 h-11 rounded flex items-center justify-center bg-white/5 border border-white/10 hover:bg-[#E50914] hover:border-[#E50914] transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={next} data-testid="gallery-next" aria-label="Suivant"
              className="w-11 h-11 rounded flex items-center justify-center bg-white/5 border border-white/10 hover:bg-[#E50914] hover:border-[#E50914] transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[16/10] bg-[#0A0A0A] select-none" data-testid="before-after-slider">
            <img src={current.after} alt="Apres" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable="false" />
            <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
              <img src={current.before} alt="Avant" className="absolute inset-0 w-full h-full object-cover" draggable="false" />
            </div>

            <div className="absolute top-5 left-5 px-3 py-1.5 bg-black/70 backdrop-blur-md border border-white/20 rounded text-[10px] uppercase tracking-[0.25em] font-bold text-white">Avant</div>
            <div className="absolute top-5 right-5 px-3 py-1.5 bg-[#E50914] border border-[#E50914] rounded text-[10px] uppercase tracking-[0.25em] font-bold text-white">Apres</div>

            <div className="absolute bottom-5 left-5 flex items-center gap-2">
              <Camera className="w-4 h-4 text-white/80" />
              <span className="text-sm font-semibold text-white">{current.title}</span>
            </div>

            <div className="absolute top-0 bottom-0 w-1 bg-[#E50914] shadow-[0_0_20px_rgba(229,9,20,0.8)]" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#E50914] border-4 border-white flex items-center justify-center shadow-2xl">
                <ChevronLeft className="w-4 h-4 text-white -mr-1" strokeWidth={3} />
                <ChevronRight className="w-4 h-4 text-white -ml-1" strokeWidth={3} />
              </div>
            </div>

            <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(parseInt(e.target.value))}
              data-testid="before-after-slider-range" className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize" />
          </div>

          <div className="mt-5 flex justify-center gap-2">
            {gallery.map((_, i) => (
              <button key={i} onClick={() => { setIdx(i); setPos(50); }} data-testid={`gallery-dot-${i}`} aria-label={`Image ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-10 bg-[#E50914]' : 'w-4 bg-white/20 hover:bg-white/40'}`} />
            ))}
          </div>
        </div>

        {albums.length > 0 && (
          <div className="mt-20">
            <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">/ Nos realisations</div>
            <h3 className="text-3xl sm:text-4xl font-black tracking-tighter mb-8">Classeurs avant / apres.</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {albums.map((a) => (
                <button key={a.id} type="button" onClick={() => setOpenAlbum(a)} data-testid={`album-${a.id}`}
                  className="group text-left rounded-xl overflow-hidden border border-white/10 bg-[#0A0A0A] hover:border-[#E50914]/50 hover:-translate-y-1 transition-all">
                  <div className="relative aspect-[4/3] overflow-hidden bg-white/5">
                    {a.cover && <img src={a.cover} alt={a.vehicle || a.service} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 bg-black/70 rounded text-[10px] font-bold text-white">
                      <FolderOpen className="w-3 h-3" /> {(a.photos || []).length}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-white text-sm leading-tight truncate">{a.vehicle || "Vehicule"}</div>
                    <div className="text-xs text-white/50 truncate">{a.service || "Prestation"}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {openAlbum && <AlbumModal album={openAlbum} onClose={() => setOpenAlbum(null)} />}
    </section>
  );
}

function AlbumModal({ album, onClose }) {
  const [big, setBig] = useState(null);
  const photos = album.photos || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="album-modal">
      <div className="min-h-full flex items-start justify-center p-4 sm:p-8">
        <div className="w-full max-w-4xl bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <div className="flex items-center gap-3 min-w-0">
              <Car className="w-5 h-5 text-[#E50914] shrink-0" />
              <div className="min-w-0">
                <div className="font-black text-white truncate">{album.vehicle || "Vehicule"}</div>
                <div className="text-xs text-white/50 truncate">{album.service || "Prestation"}</div>
              </div>
            </div>
            <button onClick={onClose} aria-label="Fermer" className="w-9 h-9 rounded flex items-center justify-center bg-white/5 border border-white/15 hover:border-[#E50914] transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5">
            {photos.length === 0 ? (
              <p className="text-white/40 text-sm text-center py-8">Aucune photo dans ce classeur.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {photos.map((url, i) => (
                  <button key={i} type="button" onClick={() => setBig(url)} className="rounded-lg overflow-hidden border border-white/10 aspect-square hover:border-[#E50914]/50 transition-colors">
                    <img src={url} alt={`Photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {big && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4" onClick={() => setBig(null)}>
          <img src={big} alt="Agrandissement" className="max-w-full max-h-full object-contain rounded-lg" />
          <button onClick={() => setBig(null)} aria-label="Fermer" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
